import { DropOverlay } from "@/components/common/DropOverlay/DropOverlay"
import { PermissionGate } from "@/components/common/PermissionGate"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent } from "@/components/ui/sheet"
import { PERMISSIONS } from "@/constants/permissions"
import { PilotHandoffPanel } from "@/features/company-index/PilotHandoffPanel"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { UploadCloud } from "lucide-react"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import {
  useDeclarationStats,
  useDeclarationsWithPolling,
} from "../../api/declarationsApi"
import type { Declaration } from "../../api/types"
import { ModuleHeader } from "../shared/ModuleHeader"
import { DeclarationImportCard } from "./DeclarationImportCard"
import { DeclarationListV2 } from "./DeclarationListV2"
import { DeclarationPanel } from "./DeclarationPanel"
import { DECLARATION_ACCEPTED_EXTENSIONS } from "./declarationFileTypes"
import { listParamsFromSearch, listSearchString } from "./listParams"
import { declarationImportPath } from "./routes"
import { useDeclarationImport } from "./useDeclarationImport"

// The URL filter/sort params `DeclarationListV2` itself owns (see that
// file's own `hasUrlFilter`/`hasFacetOrSortActive`) — everything BUT its
// component-local free-text search box, which lives nowhere this module can
// read it. Read here only to gate the first-run empty state below: with any
// of these present, a genuinely empty result is a FILTERED empty (that
// component's own concern), never "the org has never uploaded anything".
const LIST_FILTER_PARAMS = [
  "supplierIds",
  "supplier_id",
  "docTypes",
  "frameworks",
  "lifecycle",
  "declaration",
  "supplier",
  "quality",
  "sortBy",
  "sortDir",
] as const

export function DeclarationsModule() {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [viewingRow, setViewingRow] = useState<Declaration | null>(null)
  // The panel's own document-preview section (A8) widens the Sheet while
  // expanded — reset whenever the sheet closes or switches to a different
  // declaration, so the next row never opens pre-widened for a section it
  // hasn't shown yet.
  const [docExpanded, setDocExpanded] = useState(false)

  // B6 — the first-run empty state belongs HERE, not in `DeclarationListV2`
  // (that file's own comment on its filtered-empty row explicitly disowns
  // it: "the org genuinely has zero declarations... a different (unfiltered)
  // empty state this component doesn't own an opinion on"). Both reads are
  // ones `DeclarationListV2` already makes, under the same keys, so this costs
  // no request of its own: the list with the exact params the list sends
  // (`listParamsFromSearch`) and the stats `total` (every live declaration,
  // held copies included). Empty list AND zero total: a held-only org keeps
  // the list's own empty row, and a failed stats read (`getDeclarationStats`
  // answers `total: 0` on error) never hides a list that has rows. It used to
  // be an unfiltered `useDeclarationsWithPolling(undefined)`, which never
  // shared the list's cache entry and so fetched and polled a second full page
  // of the slow list query.
  //
  // The list read runs only while the stats total is 0, the one case the
  // check can come out true. Otherwise it would be a second query whenever the
  // list's search box adds `q` (this module cannot see it), polled every 3 s
  // while anything is in flight.
  const hasActiveListFilter = LIST_FILTER_PARAMS.some((key) =>
    searchParams.get(key),
  )
  const { data: stats } = useDeclarationStats()
  const { data: listPages } = useDeclarationsWithPolling(
    listParamsFromSearch(searchParams),
    { enabled: !hasActiveListFilter && stats?.total === 0 },
  )
  const listIsEmpty =
    listPages !== undefined &&
    !listPages.pages.some((page) => page.items.length > 0)
  const showFirstRunEmpty =
    !hasActiveListFilter && listIsEmpty && stats?.total === 0

  // Drag a declaration onto the OVERVIEW and it imports right here: same
  // pipeline as the import page (`useDeclarationImport`), progress in an
  // inline card above the list, refusals stay readable until dismissed. The
  // overlay is container-scoped: the drop target is this module body, never
  // the app chrome around it.
  const importer = useDeclarationImport()

  return (
    <div className="relative flex min-h-full flex-col gap-6">
      <DropOverlay
        scope="container"
        acceptedExtensions={DECLARATION_ACCEPTED_EXTENSIONS}
        hint={translate("MC_DECL_DROP_HINT")}
        onFilesDropped={() => {}}
        onAllFilesDropped={(files) => void importer.importFiles(files)}
      />
      {/* BOTH flags render the app's standard ModuleHeader. Studio V2 briefly
          shipped the prototype's oversized pagehead instead; the product owner
          reverted that deliberately — the headline must read like every other
          module's, and the import action belongs top right, not below the
          table. */}
      {
        <ModuleHeader
          title={translate("MC_DECL_TITLE")}
          subtitle={translate("MC_DECL_SUBTITLE")}
          helpArticle={{
            categoryId: "supplier-studio",
            articleId: "erklaerung-pruefen",
          }}
          right={
            <PermissionGate permission={PERMISSIONS.MC_DECLARATIONS_UPLOAD}>
              <Button
                data-agent-target="suppliers.declaration-upload"
                variant="default"
                size="sm"
                className="h-[30px] gap-1.5 bg-yellow1 text-[12.5px] text-black hover:bg-yellow2"
                onClick={() => navigate(declarationImportPath)}
              >
                <UploadCloud size={13} />
                {translate("MC_DECL_BTN_IMPORT")}
              </Button>
            </PermissionGate>
          }
        />
      }

      <PilotHandoffPanel />

      {importer.rows.length > 0 && (
        <DeclarationImportCard
          rows={importer.rows}
          isImporting={importer.isImporting}
          onClose={importer.reset}
        />
      )}

      {showFirstRunEmpty ? (
        <FirstRunEmptyState onImport={() => navigate(declarationImportPath)} />
      ) : (
        <DeclarationListV2
          selectedId={viewingRow?.id}
          onOpen={(row) => {
            setViewingRow((prev) => (prev?.id === row.id ? null : row))
            setDocExpanded(false)
          }}
          onReview={(row) => {
            // Relative to the current `…/declarations` route — append id/review,
            // carrying the overview's own filter/sort params (TUR-1211) so
            // Überspringen and every other "next declaration" jump out of the
            // review workspace walks the SAME order the reviewer saw here,
            // rather than the server's arbitrary no-sort default.
            navigate(`${row.id}/review${listSearchString(searchParams)}`)
          }}
        />
      )}

      <Sheet
        open={viewingRow !== null}
        onOpenChange={(open) => {
          if (!open) {
            setViewingRow(null)
            setDocExpanded(false)
          }
        }}
      >
        <SheetContent
          side="right"
          showCloseButton={false}
          overlayClassName="bg-black/40"
          className={cn(
            // `w-full` + `max-w-[…]` (rather than a bare `w-[…]`) so the sheet
            // is bounded by the viewport on a narrow screen too — a fixed
            // width alone would overflow horizontally below that width.
            "overflow-y-auto border-grey4 bg-grey1 p-0 transition-[max-width] duration-200",
            docExpanded
              ? "w-full max-w-[720px] sm:max-w-[720px]"
              : "w-full max-w-[420px] sm:max-w-[420px]",
          )}
        >
          {viewingRow && (
            // Keyed on the declaration id so switching rows while the sheet
            // stays open (the toggle above never nulls first) fully remounts
            // the panel — otherwise its local `docExpanded`/delete-confirm
            // state would leak from the row just closed onto the new one.
            <DeclarationPanel
              key={viewingRow.id}
              row={viewingRow}
              onClose={() => setViewingRow(null)}
              onDocExpandedChange={setDocExpanded}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}

/** B6 — the true first-run state: the org has never uploaded a supplier
 *  declaration at all, distinct from a filtered view that happens to find
 *  no matches (that stays `DeclarationListV2`'s own "no matches" row, with
 *  the table and its Reset affordance still on screen). Routes through the
 *  SAME import entry point as the header's own "Import Declarations"
 *  button. */
function FirstRunEmptyState({ onImport }: { onImport: () => void }) {
  const { translate } = useTranslation()
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-grey4 px-6 py-16 text-center">
      <UploadCloud size={22} className="text-grey5" aria-hidden="true" />
      <p className="max-w-[360px] text-[13px] text-grey5">
        {translate("MC_DECL_V2_LIST_FIRST_RUN_EMPTY")}
      </p>
      <Button
        variant="default"
        size="sm"
        className="h-[30px] gap-1.5 bg-yellow1 text-[12.5px] text-black hover:bg-yellow2"
        onClick={onImport}
      >
        <UploadCloud size={13} />
        {translate("MC_DECL_BTN_IMPORT")}
      </Button>
    </div>
  )
}
